import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { useCallback, useRef, useState } from 'react';
import { apiData, downloadCsv, errorMessage } from './subscriptions.shared';

/**
 * Local-state loader for a student-profile list endpoint
 * (`user/profile/:id/<path>`). `extraFilters` are appended to whatever the
 * table's own filter UI selected; `extraBody` is merged into the request.
 * The latest request is kept so an action can `reload()` the same page and
 * `exportCsv()` exports what the table currently shows (all pages).
 */
const useStudentList = <T,>(
	studentId: number | undefined,
	path: string,
	extraFilters: Array<Record<string, any>> = [],
	fallbackError = 'Unable to load data',
	extraBody: Record<string, any> = {}
) => {
	const { displaySnackbar } = useSnackbar();
	const [rows, setRows] = useState<T[]>([]);
	const [meta, setMeta] = useState<any>({});
	const [filterOptions, setFilterOptions] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const lastRequest = useRef<Partial<onPageChangeRequestMeta>>({});
	const extra = JSON.stringify(extraFilters);
	const body = JSON.stringify(extraBody);

	const load = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}) => {
			if (!studentId) return;
			lastRequest.current = request;
			setLoading(true);
			try {
				const response = await api.post(`user/profile/${studentId}/${path}`, {
					...JSON.parse(body),
					page: request.activePage || 1,
					perPage: request.perPage || 10,
					query: request.query || undefined,
					sortBy: request.sortBy || undefined,
					filters: [...(request.filters || []), ...JSON.parse(extra)],
				});
				setRows(apiData<T[]>(response) || []);
				setMeta(response.data.meta || {});
				setFilterOptions(response.data.filters || []);
			} catch (error: any) {
				displaySnackbar('error', errorMessage(error, fallbackError));
			} finally {
				setLoading(false);
			}
		},
		[studentId, path, extra, body, displaySnackbar, fallbackError]
	);

	const reload = useCallback(() => load(lastRequest.current), [load]);

	const [exporting, setExporting] = useState(false);
	const exportCsv = useCallback(async () => {
		if (!studentId) return;
		const request = lastRequest.current;
		setExporting(true);
		try {
			const response = await api.post(`user/profile/${studentId}/${path}`, {
				...JSON.parse(body),
				page: 1,
				perPage: 10,
				query: request.query || undefined,
				sortBy: request.sortBy || undefined,
				filters: [...(request.filters || []), ...JSON.parse(extra)],
				export: true,
			});
			downloadCsv(response);
		} catch (error: any) {
			displaySnackbar('error', errorMessage(error, 'Unable to export'));
		} finally {
			setExporting(false);
		}
	}, [studentId, path, extra, body, displaySnackbar]);

	return { rows, meta, filterOptions, loading, load, reload, exportCsv, exporting };
};

export default useStudentList;
